import "./ProfilePanelContent.css";
import { Alert } from "@ui/Alert";
import { Avatar } from "@ui/Avatar";
import { QuestionBox } from "@ui/organisms/QuestionBox/QuestionBox";
import {
	defaultQuestionEffort,
	type QuestionEffort,
} from "@ui/organisms/QuestionBox/questionEfforts";
import { Shimmer } from "@ui/Shimmer";
import { HStack, VStack } from "@ui/Stack";
import { Text } from "@ui/typography/Text";
import { createResource, createSignal, Match, Show, Switch } from "solid-js";
import {
	getInfluencerPublicProfile,
	type InfluencerPublicProfile,
	type ListBoardsResponse,
	listPublicBoards,
} from "@/client";
import { PublicBoards } from "~/modules/askgvt/public-profile/components/PublicBoards";
import { PublicBoardsProfileTitle } from "~/modules/askgvt/public-profile/components/PublicBoardsProfileTitle";
import { startChat } from "../../chat-panel/startChat";
import type { MentionData } from "../Mention";
import { isInfluencerMention } from "../mentionUtils";
import { ChannelSummary } from "./ChannelSummary";
import { ProfilePanelSocials } from "./ProfilePanelSocials";

const mockBoardResponse: ListBoardsResponse = {
	boards: [{ id: "123", title: "Test Board" }],
	total: 1,
	page: 1,
	page_size: 8,
	page_count: 1,
};

const mockInfluencer: InfluencerPublicProfile = {
	id: "123",
	handle: "test",
	display_name: "Test Influencer",
	profile_picture_url: "https://example.com/profile.jpg",
	platforms: [
		{
			platform: "instagram",
			handle: "test",
			profile_url: "https://example.com/profile.jpg",
			follower_count: 1000,
		},
	],
};
type Props = {
	mention: MentionData;
	/**
	 * Show only what the mention carries. The full profile is served to
	 * signed-in readers alone, and its question box asks as one.
	 */
	summaryOnly?: boolean;
};

export const ProfilePanelContent = (props: Props) => {
	const influencerId = () =>
		!props.summaryOnly && isInfluencerMention(props.mention)
			? props.mention.id
			: undefined;

	const [influencer] = createResource(influencerId, async (id) => {
		if (!id) {
			return undefined;
		}
		const res = await getInfluencerPublicProfile({
			path: { influencer_id: id },
		});
		return res.data?.influencer;
	});

	return (
		<VStack spacing="large">
			<div class="profile-panel-content__body">
				<Show
					when={influencerId()}
					fallback={<ChannelSummary mention={props.mention} />}
				>
					<Switch>
						<Match when={influencer.loading}>
							<Shimmer mockData={mockInfluencer}>
								{(data) => (
									<div style={{ padding: "1rem" }}>
										<Content influencer={data} />
									</div>
								)}
							</Shimmer>
						</Match>
						<Match when={influencer.error}>
							<VStack spacing="medium">
								<Alert variant="error">Could not load profile</Alert>
								<ChannelSummary mention={props.mention} />
							</VStack>
						</Match>
						<Match when={influencer()}>
							{(data) => <Content influencer={data()} />}
						</Match>
					</Switch>
				</Show>
			</div>
		</VStack>
	);
};

const Content = (props: { influencer: InfluencerPublicProfile }) => {
	const [effort, setEffort] = createSignal<QuestionEffort>(
		defaultQuestionEffort(),
	);
	const [boards] = createResource(async () => {
		// Don't load boards for mock influencer
		if (props.influencer.id === mockInfluencer.id) {
			return mockBoardResponse;
		}
		const res = await listPublicBoards({
			query: {
				owner_user_id: props.influencer.id,
				page_size: 8,
			},
		});
		return res.data;
	});
	return (
		<VStack spacing="large">
			<HStack spacing="medium" align="center">
				<Avatar
					src={props.influencer.profile_picture_url}
					alt={props.influencer.display_name}
				/>
				<VStack justify="space-around">
					<Text size="medium-large" weight="bold">
						{props.influencer.display_name}
					</Text>
					<Text color="fg2" size="small">
						@{props.influencer.handle}
					</Text>
				</VStack>
			</HStack>
			<Show when={props.influencer.biography}>
				<Text size="small" color="fg2">
					{props.influencer.biography}
				</Text>
			</Show>
			<Show when={props.influencer.ask_page_pool_id}>
				<QuestionBox
					onAsk={(prompt, clearQuestion) => {
						startChat({
							prompt,
							scope: "influencer_pool",
							// Resolves server-side to their public AskPage
							// pool; this used to pass their private one.
							influencer_id: props.influencer.id,
							effort: effort(),
							clearQuestion,
							onEntitlementRequired: () => {
								window.location.href = `/@${props.influencer.handle}`;
							},
						});
					}}
					showListenToUserButton={true}
					rounded={true}
					placeholder={`Ask ${props.influencer.display_name}...`}
					scope="influencer_pool"
					contextTabs={["videos"]}
					poolId={props.influencer.ask_page_pool_id ?? undefined}
					redirectURL="/studio/questions/{questionId}"
					theme="studio"
					effort={effort()}
					onEffortChange={setEffort}
				/>
			</Show>
			<ProfilePanelSocials platforms={props.influencer.platforms ?? []} />
			<VStack spacing="small">
				<PublicBoardsProfileTitle
					influencer={props.influencer}
					boards={boards}
				/>
				<PublicBoards boards={boards} influencer={props.influencer} />
			</VStack>
		</VStack>
	);
};
